import React, { useState } from 'react';
import { supabase } from '../supabaseClient';
import './AdvocateRegistration.css';
import { Loader, CheckCircle, AlertCircle, Upload, X } from 'lucide-react';

const practiceAreasList = [
  'Corporate Law',
  'Criminal Law',
  'Family Law',
  'Intellectual Property',
  'Real Estate Law',
  'Tax Law'
];

export default function AdvocateRegistration({ user }) {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    location: '',
    bar_number: '',
    practice_areas: [],
    experience: '',
    bio: '',
    photoPreview: null,
    photoFile: null
  });
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');

  function togglePractice(area) {
    setForm((f) => {
      const has = f.practice_areas.includes(area);
      return { ...f, practice_areas: has ? f.practice_areas.filter(a => a !== area) : [...f.practice_areas, area] };
    });
  }

  async function handleFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;

    // Store the actual file for upload later, and create a preview URL
    const reader = new FileReader();
    reader.onload = () => {
      setForm((f) => ({ ...f, photoPreview: reader.result, photoFile: file }));
    };
    reader.readAsDataURL(file);
  }

  function removePhoto() {
    setForm((f) => ({ ...f, photoPreview: null, photoFile: null }));
  }

  const uploadPhotoToSupabase = async (file, advocateId) => {
    const fileName = `adv_${advocateId}_${Date.now()}.png`;

    const { data, error } = await supabase.storage
      .from('advocate-photos')
      .upload(fileName, file, { contentType: file.type, upsert: true });

    if (error) throw error;

    // Get the public URL
    const { data: { publicUrl } } = supabase.storage
      .from('advocate-photos')
      .getPublicUrl(fileName);

    return publicUrl;
  };

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setMessage('');
    try {
      // Create an ID first so we can tie the photo to it (or just use a random component, Supabase handles advocate IDs automatically usually, 
      // but let's generate a temporary unique identifier for the photo name to avoid collisions, or let the DB auto-generate the ID and we upload the photo after)
      // Since it's easier to upload the photo then insert the row, we'll use a random uuid or timestamp for the photo name.
      const tempId = crypto.randomUUID ? crypto.randomUUID() : Date.now().toString();

      let photoUrl = null;
      if (form.photoFile) {
        photoUrl = await uploadPhotoToSupabase(form.photoFile, tempId);
      }

      const payload = {
        user_id: user?.id || null,
        name: form.name,
        email: form.email,
        phone: form.phone,
        location: form.location,
        bar_number: form.bar_number,
        practice_areas: form.practice_areas,
        experience: form.experience,
        bio: form.bio,
        photo_path: photoUrl // Use the actual photo path URL
      };

      const { error } = await supabase
        .from('advocates')
        .insert([payload]);

      if (!error) {
        setMessage('success');
        setTimeout(() => {
          setForm({
            name: '', email: '', phone: '', location: '', bar_number: '',
            practice_areas: [], experience: '', bio: '', photoPreview: null, photoFile: null
          });
          setMessage('');
        }, 2000);
      } else {
        console.error(error);
        setMessage('error');
      }
    } catch (err) {
      console.warn(err);
      setMessage('error');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="adv-page">
      <div className="adv-container" data-tour="Advocate Form Card">
        <h1 className="adv-title">Advocate Registration</h1>
        <p className="adv-subtitle">
          Join our network of legal professionals and help clients find expert legal counsel
        </p>

        {/* Profile Picture Section */}
        <div className="adv-profile-section">
          <p className="adv-profile-label">Profile Picture</p>
          <div className="upload-row">
            {!form.photoPreview ? (
              <label className="upload-btn">
                <Upload size={32} className="upload-icon" />
                <span className="upload-text">Click to upload or drag & drop</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                />
              </label>
            ) : (
              <div className="thumb-wrap">
                <img src={form.photoPreview} alt="Profile thumbnail" />
                <button
                  type="button"
                  className="remove-photo"
                  onClick={removePhoto}
                >
                  <X size={16} style={{ display: 'inline', marginRight: '4px' }} />
                  Remove Photo
                </button>
              </div>
            )}
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Two-Column Grid */}
          <div className="adv-grid">
            {/* Left Column */}
            <div className="adv-left">
              {/* Full Name */}
              <div className="adv-form-group">
                <label className="adv-form-label">
                  Full Name
                  <span className="required">*</span>
                </label>
                <input
                  type="text"
                  className="adv-input"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Enter your full name"
                  disabled={submitting}
                />
              </div>

              {/* Email Address */}
              <div className="adv-form-group">
                <label className="adv-form-label">
                  Email Address
                  <span className="required">*</span>
                </label>
                <input
                  type="email"
                  className="adv-input"
                  value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="your@email.com"
                  disabled={submitting}
                />
              </div>

              {/* Phone Number */}
              <div className="adv-form-group">
                <label className="adv-form-label">
                  Phone Number
                  <span className="required">*</span>
                </label>
                <input
                  type="tel"
                  className="adv-input"
                  value={form.phone}
                  onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                  placeholder="+1 (555) 123-4567"
                  disabled={submitting}
                />
              </div>

              {/* Location */}
              <div className="adv-form-group">
                <label className="adv-form-label">Location</label>
                <input
                  type="text"
                  className="adv-input"
                  value={form.location}
                  onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
                  placeholder="City, State"
                  disabled={submitting}
                />
              </div>

              {/* Callout Section - Bar Number & Experience */}
              <div className="callout-section">
                {/* Bar Number */}
                <div className="adv-form-group">
                  <label className="adv-form-label">
                    Bar Number
                    <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    className="adv-input"
                    value={form.bar_number}
                    onChange={e => setForm(f => ({ ...f, bar_number: e.target.value }))}
                    placeholder="Your bar number"
                    disabled={submitting}
                  />
                </div>

                {/* Experience */}
                <div className="adv-form-group">
                  <label className="adv-form-label">Years of Experience</label>
                  <input
                    type="text"
                    className="adv-input"
                    value={form.experience}
                    onChange={e => setForm(f => ({ ...f, experience: e.target.value }))}
                    placeholder="e.g., 5+ years"
                    disabled={submitting}
                  />
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="adv-right">
              {/* Biography */}
              <div className="adv-form-group">
                <label className="adv-form-label">Biography</label>
                <textarea
                  className="adv-textarea"
                  value={form.bio}
                  onChange={e => setForm(f => ({ ...f, bio: e.target.value }))}
                  placeholder="Tell us about your legal expertise and background..."
                  disabled={submitting}
                />
              </div>

              {/* Practice Areas */}
              <div className="practice-area">
                <label className="practice-area-label">
                  Practice Areas
                  <span className="required">*</span>
                </label>
                <div className="practice-list">
                  {practiceAreasList.map((p) => (
                    <div key={p} className="practice-item">
                      <input
                        type="checkbox"
                        id={`practice-${p}`}
                        checked={form.practice_areas.includes(p)}
                        onChange={() => togglePractice(p)}
                        disabled={submitting}
                      />
                      <label htmlFor={`practice-${p}`}>{p}</label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="adv-button-group">
            <button
              type="submit"
              className="create-btn"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Loader size={18} className="spinner" />
                  Registering...
                </>
              ) : (
                'Complete Registration'
              )}
            </button>
            <button
              type="button"
              className="cancel-btn"
              onClick={() => window.history.back()}
              disabled={submitting}
            >
              Cancel
            </button>
          </div>

          {/* Messages */}
          {message === 'success' && (
            <div className="adv-message success">
              <CheckCircle size={18} />
              <span>✓ Registration successful! Redirecting...</span>
            </div>
          )}
          {message === 'error' && (
            <div className="adv-message error">
              <AlertCircle size={18} />
              <span>✗ Please fill in all required fields or check your information</span>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}